import type {BillingGranularity, BillingInfoItem} from "../../AdminTypes";
import {BillingMetricKey, formatBucketLabel, formatMetricValue} from "../billingUtils";
import {Box, Stack, Typography} from "@mui/material";
import {alpha} from "@mui/material/styles";

type BarChartBarProps = {
    item: BillingInfoItem;
    activeGranularity: BillingGranularity;
    maxMetricValue: number;
    selectedMetric: BillingMetricKey;
};

export const BarChartBar = (
    {
        item,
        activeGranularity,
        maxMetricValue,
        selectedMetric
    }: BarChartBarProps) => {
    const metricValue = selectedMetric === "price" ? item.price : item.totalTokenAmount;
    const heightPercentage = Math.max((metricValue / maxMetricValue) * 100, 10);
    const bucketLabel = formatBucketLabel(item.date, activeGranularity);

    return (
        <Stack key={item.date} spacing={1} alignItems="stretch" sx={{minWidth: 0}}>
            <Box
                sx={{
                    height: 240,
                    display: "flex",
                    alignItems: "flex-end",
                }}
            >
                <Box
                    sx={(theme) => ({
                        width: "100%",
                        height: `${heightPercentage}%`,
                        minHeight: 24,
                        borderRadius: 3,
                        background:
                            selectedMetric === "price"
                                ? `linear-gradient(180deg, ${alpha(theme.palette.secondary.light, 0.92)}, ${theme.palette.secondary.main})`
                                : `linear-gradient(180deg, ${alpha(theme.palette.primary.light, 0.92)}, ${theme.palette.primary.main})`,
                        border: "1px solid",
                        borderColor:
                            selectedMetric === "price"
                                ? alpha(theme.palette.secondary.dark, 0.22)
                                : alpha(theme.palette.primary.dark, 0.22),
                        boxShadow:
                            selectedMetric === "price"
                                ? `0 14px 26px ${alpha(theme.palette.secondary.main, 0.18)}`
                                : `0 14px 26px ${alpha(theme.palette.primary.main, 0.18)}`,
                    })}
                />
            </Box>
            <Typography
                sx={{
                    fontWeight: 700,
                    textAlign: "center",
                    color: "text.primary",
                }}
            >
                {bucketLabel}
            </Typography>
            <Typography variant="body2" sx={{textAlign: "center", color: "text.secondary"}}>
                {formatMetricValue(selectedMetric, metricValue)}
            </Typography>
        </Stack>
    );
};
